import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { CategoryTabs } from '@/components/ui/CategoryTabs';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { SeverityBadge } from '@/components/ui/SeverityBadge';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  TrendingUp, 
  BarChart3, 
  TrendingDown,
  RefreshCw,
  MoreVertical,
  ExternalLink,
  X,
  Filter
} from 'lucide-react';
import { getProjectInsights, updateInsightStatus, type Insight } from '@/api/insightsApi';
import { ImpactVisualization } from './components/ImpactVisualization';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { PanelLoading } from '@/components/ui/loading';

export const InsightsWorkspace: React.FC = () => {
  const { selectedProject } = useGlobalProject();
  const { theme } = useAppTheme();
  const navigate = useNavigate();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<string | null>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    priority: 'all',
    status: 'all',
    source: 'all',
    scope: 'all',
    criticalPathOnly: false,
    milestonesOnly: false,
  });

  // Load insights from API and apply localStorage statuses
  useEffect(() => {
    const loadInsights = async () => {
      if (!selectedProject?.id) {
        setInsights([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await getProjectInsights(selectedProject.id);
        
        // Apply stored statuses from localStorage
        const storageKey = `insight_statuses_${selectedProject.id}`;
        const storedStatuses = localStorage.getItem(storageKey);
        if (storedStatuses) {
          try {
            const statusMap = JSON.parse(storedStatuses);
            const dataWithStatuses = data.map(insight => {
              const key = `${insight.title}_${insight.type}`;
              if (statusMap[key]) {
                return { ...insight, status: statusMap[key] };
              }
              return insight;
            });
            setInsights(dataWithStatuses);
          } catch (err) {
            console.error('Error loading insight statuses from localStorage:', err);
            setInsights(data);
          }
        } else {
          setInsights(data);
        }
      } catch (err) {
        console.error('Error loading insights:', err);
        setError('Failed to load insights. Please try again.');
        setInsights([]);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, [selectedProject?.id]);

  const handleRefresh = async () => {
    if (!selectedProject?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectInsights(selectedProject.id);
      
      // Apply stored statuses from localStorage
      const storageKey = `insight_statuses_${selectedProject.id}`;
      const storedStatuses = localStorage.getItem(storageKey);
      if (storedStatuses) {
        try {
          const statusMap = JSON.parse(storedStatuses);
          const dataWithStatuses = data.map(insight => {
            const key = `${insight.title}_${insight.type}`;
            if (statusMap[key]) {
              return { ...insight, status: statusMap[key] };
            }
            return insight;
          });
          setInsights(dataWithStatuses);
        } catch (err) {
          console.error('Error loading insight statuses from localStorage:', err);
          setInsights(data);
        }
      } else {
        setInsights(data);
      }
    } catch (err) {
      console.error('Error refreshing insights:', err);
      setError('Failed to refresh insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (insightId: string, newStatus: 'New' | 'Reviewed' | 'Actioned') => {
    try {
      await updateInsightStatus(insightId, newStatus);
      
      // Find the insight to get its title and type for storage key
      const insight = insights.find(i => i.id === insightId);
      if (insight && selectedProject?.id) {
        // Store status in localStorage keyed by project + title + type
        const storageKey = `insight_statuses_${selectedProject.id}`;
        const storedStatuses = localStorage.getItem(storageKey);
        const statusMap = storedStatuses ? JSON.parse(storedStatuses) : {};
        const key = `${insight.title}_${insight.type}`;
        statusMap[key] = newStatus;
        localStorage.setItem(storageKey, JSON.stringify(statusMap));
      }
      
      // Update local state
      setInsights(prev => prev.map(insight => 
        insight.id === insightId ? { ...insight, status: newStatus } : insight
      ));
    } catch (err) {
      console.error('Error updating insight status:', err);
    }
  };

  const handleViewMilestones = () => {
    if (!selectedProject?.id) return;
    navigate(`/app/activities?project=${selectedProject.id}&filter=milestones&atRisk=true`);
  };

  const handleViewActivities = (insight: Insight) => {
    if (!selectedProject?.id) return;
    
    let filterParams = `project=${selectedProject.id}`;
    
    if (insight.title.includes('Schedule Delay') || insight.title.includes('Overdue')) {
      filterParams += '&overdue=true';
    } else if (insight.title.includes('Negative Float')) {
      filterParams += '&negativeFloat=true';
    } else if (insight.title.includes('Open Ends')) {
      filterParams += '&openEnds=true';
    } else if (insight.title.includes('Critical')) {
      filterParams += '&critical=true';
    }
    
    navigate(`/app/activities?${filterParams}`);
  };

  const handleOpenInGantt = () => {
    if (!selectedProject?.id) return;
    navigate(`/app/projects/${selectedProject.id}/activities`);
  };

  const handleGenerateRecovery = () => {
    if (!selectedProject?.id) return;
    navigate(`/app/reports?project=${selectedProject.id}&template=recovery`);
  };

  const handleSelectInsight = (insightId: string) => {
    setSelectedInsight(insightId);
    setShowDetailPanel(true);
  };

  // Calculate health metrics
  const healthMetrics = useMemo(() => {
    const total = insights.length;
    const critical = insights.filter((i) => i.priority === 'High').length;
    const high = insights.filter((i) => i.priority === 'High').length;
    const medium = insights.filter((i) => i.priority === 'Medium').length;
    const low = insights.filter((i) => i.priority === 'Low').length;
    const milestonesAtRisk = insights.reduce((sum, i) => sum + (i.milestonesAffected || 0), 0);
    const negativeFloat = insights.filter((i) => i.finishDelta && i.finishDelta < 0).length;
    const openEnds = insights.filter((i) => 
      i.type === 'Risk' && i.title.toLowerCase().includes('open ends')
    ).reduce((sum, i) => sum + (i.activitiesAffected || 0), 0);

    return {
      total,
      critical,
      high,
      medium,
      low,
      milestonesAtRisk,
      negativeFloat,
      openEnds,
    };
  }, [insights]);

  // Filter insights
  const filteredInsights = useMemo(() => {
    return insights.filter((insight) => {
      if (filters.type !== 'all' && insight.type !== filters.type) return false;
      if (filters.priority !== 'all' && insight.priority !== filters.priority) return false;
      if (filters.status !== 'all' && insight.status !== filters.status) return false;
      if (filters.source !== 'all' && insight.source !== filters.source) return false;
      if (filters.scope !== 'all' && insight.scope !== filters.scope) return false;
      if (filters.criticalPathOnly && insight.scope !== 'WBS') return false;
      if (filters.milestonesOnly && !insight.milestonesAffected) return false;
      return true;
    });
  }, [insights, filters]);

  const selectedInsightData = selectedInsight
    ? insights.find((i) => i.id === selectedInsight)
    : null;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Risk':
        return <AlertTriangle className="h-4 w-4 text-[#EF4444]" />;
      case 'Opportunity':
        return <TrendingUp className="h-4 w-4 text-[#22C55E]" />;
      case 'Performance':
        return <BarChart3 className="h-4 w-4 text-[#1D4ED8]" />;
      case 'trend':
        return <TrendingDown className="h-4 w-4 text-[#1D4ED8]" />;
      default:
        return <BarChart3 className={`h-4 w-4 ${
          theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-500'
        }`} />;
    }
  };

  const getPrioritySeverity = (priority: string): 'High' | 'Medium' | 'Low' => {
    switch (priority) {
      case 'High':
      case 'Critical':
        return 'High';
      case 'Medium':
        return 'Medium';
      case 'Low':
        return 'Low';
      default:
        return 'Low';
    }
  };

  const tabCategories = [
    { id: 'summary', label: 'Summary' },
    { id: 'impact', label: 'Impact' },
    { id: 'history', label: 'History' },
  ];

  const activeTab = 'summary';

  return (
    <div className={`flex flex-col h-full ${
      theme === 'dark' ? 'bg-[#030712]' : 'bg-white'
    }`}>
      {/* Top Health Strip */}
      <div className="w-full px-6">
        <DarkPanel className="mt-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-display font-semibold ${
              theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
            }`}>Insights Health</h2>
            <Button
              variant="outline"
              size="sm"
              className={theme === 'dark'
                ? 'border-[#111827] text-[#E5E7EB] hover:bg-[#050B17]'
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }
              onClick={handleRefresh}
              disabled={loading || !selectedProject}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Insights
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <DarkPanel nested padding="sm">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Total Insights</div>
              <div className={`text-lg  font-bold ${
                theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
              }`}>
                {loading ? '--' : healthMetrics.total}
              </div>
            </DarkPanel>
            <DarkPanel nested padding="sm" className="border-[#EF4444]/50">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Critical</div>
              <div className="text-lg  font-bold text-[#EF4444]">
                {loading ? '--' : healthMetrics.critical}
              </div>
            </DarkPanel>
            <DarkPanel nested padding="sm" className="border-[#FACC15]/50">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>High</div>
              <div className="text-lg  font-bold text-[#FACC15]">
                {loading ? '--' : healthMetrics.high}
              </div>
            </DarkPanel>
            <DarkPanel nested padding="sm" className="border-[#1D4ED8]/50">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Medium</div>
              <div className="text-lg  font-bold text-[#1D4ED8]">
                {loading ? '--' : healthMetrics.medium}
              </div>
            </DarkPanel>
            <DarkPanel nested padding="sm" className="border-[#22C55E]/50">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Low</div>
              <div className="text-lg  font-bold text-[#22C55E]">
                {loading ? '--' : healthMetrics.low}
              </div>
            </DarkPanel>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <DarkPanel nested padding="sm">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Milestones at Risk</div>
              <div className={`text-lg  font-bold ${
                theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
              }`}>
                {loading ? '--' : healthMetrics.milestonesAtRisk}
              </div>
            </DarkPanel>
            <DarkPanel nested padding="sm" className="border-[#EF4444]/50">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Negative Float</div>
              <div className="text-lg  font-bold text-[#EF4444]">
                {loading ? '--' : healthMetrics.negativeFloat}
              </div>
            </DarkPanel>
            <DarkPanel nested padding="sm">
              <div className={`text-xs  mb-2 ${
                theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
              }`}>Open Ends</div>
              <div className={`text-lg  font-bold ${
                theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
              }`}>
                {loading ? '--' : healthMetrics.openEnds}
              </div>
            </DarkPanel>
          </div>
        </DarkPanel>

        {/* Horizontal Filter Bar */}
        <DarkPanel className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <SectionLabel showBorder={false}>Filters</SectionLabel>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={theme === 'dark'
                ? 'text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#050B17]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-[#111827]">
              <div>
                <label className={`text-[10px]  mb-1 block ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                }`}>Type</label>
                <Select
                  value={filters.type}
                  onValueChange={(value) => setFilters({ ...filters, type: value })}
                >
                  <SelectTrigger className={`h-8 text-xs  ${
                    theme === 'dark'
                      ? 'bg-[#050B17] border-[#111827] text-[#F9FAFB]'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-[#040816] border-[#111827]' : 'bg-white border-gray-200'}>
                    <SelectItem value="all" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>All Types</SelectItem>
                    <SelectItem value="Risk" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Risk</SelectItem>
                    <SelectItem value="Opportunity" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Opportunity</SelectItem>
                    <SelectItem value="Performance" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Performance</SelectItem>
                    <SelectItem value="trend" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Trend</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={`text-[10px]  mb-1 block ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                }`}>Priority</label>
                <Select
                  value={filters.priority}
                  onValueChange={(value) => setFilters({ ...filters, priority: value })}
                >
                  <SelectTrigger className={`h-8 text-xs  ${
                    theme === 'dark'
                      ? 'bg-[#050B17] border-[#111827] text-[#F9FAFB]'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-[#040816] border-[#111827]' : 'bg-white border-gray-200'}>
                    <SelectItem value="all" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>All Priorities</SelectItem>
                    <SelectItem value="High" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>High</SelectItem>
                    <SelectItem value="Medium" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Medium</SelectItem>
                    <SelectItem value="Low" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={`text-[10px]  mb-1 block ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                }`}>Status</label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger className={`h-8 text-xs  ${
                    theme === 'dark'
                      ? 'bg-[#050B17] border-[#111827] text-[#F9FAFB]'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-[#040816] border-[#111827]' : 'bg-white border-gray-200'}>
                    <SelectItem value="all" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>All Status</SelectItem>
                    <SelectItem value="New" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>New</SelectItem>
                    <SelectItem value="Reviewed" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Reviewed</SelectItem>
                    <SelectItem value="Actioned" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Actioned</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={`text-[10px]  mb-1 block ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                }`}>Source</label>
                <Select
                  value={filters.source}
                  onValueChange={(value) => setFilters({ ...filters, source: value })}
                >
                  <SelectTrigger className={`h-8 text-xs  ${
                    theme === 'dark'
                      ? 'bg-[#050B17] border-[#111827] text-[#F9FAFB]'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-[#040816] border-[#111827]' : 'bg-white border-gray-200'}>
                    <SelectItem value="all" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>All Sources</SelectItem>
                    <SelectItem value="AI" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>AI</SelectItem>
                    <SelectItem value="Rule" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Rule</SelectItem>
                    <SelectItem value="Manual" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={`text-[10px]  mb-1 block ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                }`}>Scope</label>
                <Select
                  value={filters.scope}
                  onValueChange={(value) => setFilters({ ...filters, scope: value })}
                >
                  <SelectTrigger className={`h-8 text-xs  ${
                    theme === 'dark'
                      ? 'bg-[#050B17] border-[#111827] text-[#F9FAFB]'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-[#040816] border-[#111827]' : 'bg-white border-gray-200'}>
                    <SelectItem value="all" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>All Scopes</SelectItem>
                    <SelectItem value="project" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Project</SelectItem>
                    <SelectItem value="wbs" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>WBS</SelectItem>
                    <SelectItem value="resource" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Resource</SelectItem>
                    <SelectItem value="calendar" className={theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'}>Calendar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 pt-3 border-t border-[#111827] mt-3">
            <label className={`flex items-center gap-2 text-xs  cursor-pointer ${
              theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
            }`}>
              <input
                type="checkbox"
                checked={filters.criticalPathOnly}
                onChange={(e) => setFilters({ ...filters, criticalPathOnly: e.target.checked })}
                className={`rounded border ${
                  theme === 'dark' ? 'border-[#111827] bg-[#050B17]' : 'border-gray-300 bg-white'
                }`}
              />
              <span>Critical path only</span>
            </label>
            <label className={`flex items-center gap-2 text-xs  cursor-pointer ${
              theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
            }`}>
              <input
                type="checkbox"
                checked={filters.milestonesOnly}
                onChange={(e) => setFilters({ ...filters, milestonesOnly: e.target.checked })}
                className={`rounded border ${
                  theme === 'dark' ? 'border-[#111827] bg-[#050B17]' : 'border-gray-300 bg-white'
                }`}
              />
              <span>Milestones only</span>
            </label>
          </div>
        </DarkPanel>
      </div>

      {/* Main Content Area - Dense Table */}
      <div className={`flex-1 overflow-hidden ${
        theme === 'dark' ? 'bg-[#030712]' : 'bg-white'
      }`}>
        <div className="w-full px-6 h-full flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <PanelLoading message="Loading insights..." />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-[#EF4444]" />
                <p className={`mb-2 text-xs  ${
                  theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                }`}>{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  className={theme === 'dark'
                    ? 'mt-4 border-[#111827] text-[#E5E7EB] hover:bg-[#050B17]'
                    : 'mt-4 border-gray-300 text-gray-700 hover:bg-gray-100'
                  }
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          ) : !selectedProject ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <AlertTriangle className={`h-12 w-12 mx-auto mb-4 ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-400'
                }`} />
                <p className={`mb-2 text-xs  ${
                  theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                }`}>No project selected</p>
                <p className={`text-[10px]  ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-500'
                }`}>Please select a project to view insights</p>
              </div>
            </div>
          ) : filteredInsights.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <AlertTriangle className={`h-12 w-12 mx-auto mb-4 ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-400'
                }`} />
                <p className={`mb-2 text-xs  ${
                  theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                }`}>No insights available</p>
                <p className={`text-[10px]  ${
                  theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-500'
                }`}>
                  {insights.length === 0 
                    ? 'No insights found for this project'
                    : 'No insights match your current filters'}
                </p>
              </div>
            </div>
          ) : (
            <DarkPanel className="flex-1 overflow-hidden flex flex-col">
              <div className="overflow-x-auto flex-1">
                <table className="w-full">
                  <thead className={theme === 'dark' ? 'bg-[#050B17]' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Type</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Title</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Priority</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Status</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Scope</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Impact</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>Date</th>
                      <th className={`px-3 py-2 text-left text-[10px]  uppercase tracking-wider ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}></th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${
                    theme === 'dark' ? 'divide-[#111827]' : 'divide-gray-200'
                  }`}>
                    {filteredInsights.map((insight) => (
                      <tr
                        key={insight.id}
                        onClick={() => handleSelectInsight(insight.id)}
                        className={`cursor-pointer transition-colors ${
                          selectedInsight === insight.id
                            ? theme === 'dark'
                              ? 'bg-[#050B17] border-l-2 border-[#1D4ED8]'
                              : 'bg-blue-50 border-l-2 border-blue-500'
                            : theme === 'dark'
                              ? 'hover:bg-[#050B17]'
                              : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            {getTypeIcon(insight.type)}
                          </div>
                        </td>
                        <td className={`px-3 py-2 text-xs  ${
                          theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                        }`}>
                          {insight.title}
                        </td>
                        <td className="px-3 py-2">
                          <SeverityBadge severity={getPrioritySeverity(insight.priority)}>
                            {insight.priority}
                          </SeverityBadge>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={theme === 'dark'
                            ? 'bg-[#050B17] border-[#111827] text-[#E5E7EB] text-[10px] '
                            : 'bg-gray-100 border-gray-300 text-gray-700 text-[10px] '
                          }>
                            {insight.status || 'New'}
                          </Badge>
                        </td>
                        <td className={`px-3 py-2 text-[10px]  ${
                          theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                        }`}>
                          {insight.scope}
                        </td>
                        <td className={`px-3 py-2 text-[10px]  max-w-xs truncate ${
                          theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                        }`}>
                          {insight.impact}
                        </td>
                        <td className={`px-3 py-2 text-[10px]  ${
                          theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                        }`}>
                          {new Date(insight.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 w-6 p-0 ${
                              theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-500'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectInsight(insight.id);
                            }}
                          >
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DarkPanel>
          )}
        </div>
      </div>

      {/* Slide-over Detail Panel */}
      {showDetailPanel && selectedInsightData && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => {
              setShowDetailPanel(false);
              setSelectedInsight(null);
            }}
          />
          {/* Slide-over Panel */}
          <div className={`fixed right-0 top-0 h-full w-[480px] ${
            theme === 'dark' ? 'bg-[#040816] border-l border-[#111827]' : 'bg-white border-l border-gray-200'
          } z-50 shadow-xl transform transition-transform duration-300 ease-out`}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                theme === 'dark' ? 'border-[#111827]' : 'border-gray-200'
              }`}>
                <h3 className={`text-lg font-display font-semibold ${
                  theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                }`}>Insight Details</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowDetailPanel(false);
                    setSelectedInsight(null);
                  }}
                  className={theme === 'dark'
                    ? 'text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#050B17]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <Tabs defaultValue="summary" className="flex flex-col h-full">
                  <div className={`px-4 py-3 border-b ${
                    theme === 'dark' ? 'border-[#111827] bg-[#040816]' : 'border-gray-200 bg-white'
                  }`}>
                    <CategoryTabs
                      categories={tabCategories}
                      activeCategory={activeTab}
                      onCategoryChange={(id) => {
                        // Tab switching logic would go here
                      }}
                    />
                  </div>

                  <TabsContent value="summary" className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        {getTypeIcon(selectedInsightData.type)}
                        <div>
                          <h3 className={`text-lg font-display font-semibold ${
                            theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                          }`}>{selectedInsightData.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <SeverityBadge severity={getPrioritySeverity(selectedInsightData.priority)}>
                              {selectedInsightData.priority}
                            </SeverityBadge>
                            <Badge variant="outline" className={theme === 'dark'
                              ? 'bg-[#050B17] border-[#111827] text-[#E5E7EB]'
                              : 'bg-gray-100 border-gray-300 text-gray-700'
                            }>
                              {selectedInsightData.type}
                            </Badge>
                            <Badge variant="outline" className={theme === 'dark'
                              ? 'bg-[#050B17] border-[#111827] text-[#E5E7EB]'
                              : 'bg-gray-100 border-gray-300 text-gray-700'
                            }>
                              {selectedInsightData.status || 'New'}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className={`rounded-lg p-3 ${
                          theme === 'dark' ? 'bg-[#050B17]' : 'bg-gray-50'
                        }`}>
                          <div className={`text-xs mb-1  ${
                            theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                          }`}>Finish Delta</div>
                          <div className={`text-lg font-semibold ${
                            selectedInsightData.finishDelta && selectedInsightData.finishDelta < 0 ? 'text-[#EF4444]' : 
                            selectedInsightData.finishDelta && selectedInsightData.finishDelta > 0 ? 'text-[#22C55E]' : 
                            theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                          }`}>
                            {selectedInsightData.finishDelta !== undefined && selectedInsightData.finishDelta !== null 
                              ? `${selectedInsightData.finishDelta >= 0 ? '+' : ''}${selectedInsightData.finishDelta.toFixed(1)} days` 
                              : 'N/A'}
                          </div>
                        </div>
                        <div className={`rounded-lg p-3 ${
                          theme === 'dark' ? 'bg-[#050B17]' : 'bg-gray-50'
                        }`}>
                          <div className={`text-xs mb-1  ${
                            theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                          }`}>Milestones Affected</div>
                          <div className={`text-lg font-semibold ${
                            theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                          }`}>
                            {selectedInsightData.milestonesAffected || 0}
                          </div>
                          {(selectedInsightData.milestonesAffected || 0) > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-xs text-[#1D4ED8] hover:text-[#1E40AF] hover:bg-[#1D4ED8]/10"
                              onClick={handleViewMilestones}
                            >
                              View Milestones →
                            </Button>
                          )}
                        </div>
                        <div className={`rounded-lg p-3 ${
                          theme === 'dark' ? 'bg-[#050B17]' : 'bg-gray-50'
                        }`}>
                          <div className={`text-xs mb-1  ${
                            theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                          }`}>Activities Affected</div>
                          <div className={`text-lg font-semibold ${
                            theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                          }`}>
                            {selectedInsightData.activitiesAffected || 0}
                          </div>
                          {(selectedInsightData.activitiesAffected || 0) > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-xs text-[#1D4ED8] hover:text-[#1E40AF] hover:bg-[#1D4ED8]/10"
                              onClick={() => handleViewActivities(selectedInsightData)}
                            >
                              View Activities →
                            </Button>
                          )}
                        </div>
                        {selectedInsightData.confidence !== undefined && (
                          <div className={`rounded-lg p-3 ${
                            theme === 'dark' ? 'bg-[#050B17]' : 'bg-gray-50'
                          }`}>
                            <div className={`text-xs mb-1  ${
                              theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                            }`}>Confidence</div>
                            <div className={`text-lg font-semibold ${
                              theme === 'dark' ? 'text-[#F9FAFB]' : 'text-gray-900'
                            }`}>
                              {Math.round(selectedInsightData.confidence * 100)}%
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className={`text-sm font-semibold mb-2  ${
                            theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                          }`}>Impact Statement</h4>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                          }`}>{selectedInsightData.impact}</p>
                        </div>
                        <div>
                          <h4 className={`text-sm font-semibold mb-2  ${
                            theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                          }`}>Description</h4>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                          }`}>{selectedInsightData.description}</p>
                        </div>
                        <div>
                          <h4 className={`text-sm font-semibold mb-2  ${
                            theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                          }`}>Recommendation</h4>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                          }`}>{selectedInsightData.recommendation}</p>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-6">
                        <Button
                          variant="outline"
                          className={theme === 'dark'
                            ? 'flex-1 border-[#111827] text-[#E5E7EB] hover:bg-[#050B17]'
                            : 'flex-1 border-gray-300 text-gray-700 hover:bg-gray-100'
                          }
                          onClick={() => handleUpdateStatus(selectedInsightData.id, 'Reviewed')}
                          disabled={selectedInsightData.status === 'Reviewed' || selectedInsightData.status === 'Actioned'}
                        >
                          Mark as Reviewed
                        </Button>
                        <Button
                          variant="outline"
                          className={theme === 'dark'
                            ? 'flex-1 border-[#111827] text-[#E5E7EB] hover:bg-[#050B17]'
                            : 'flex-1 border-gray-300 text-gray-700 hover:bg-gray-100'
                          }
                          onClick={() => handleUpdateStatus(selectedInsightData.id, 'Actioned')}
                          disabled={selectedInsightData.status === 'Actioned'}
                        >
                          Mark as Actioned
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          className={theme === 'dark'
                            ? 'flex-1 border-[#111827] text-[#E5E7EB] hover:bg-[#050B17]'
                            : 'flex-1 border-gray-300 text-gray-700 hover:bg-gray-100'
                          }
                          onClick={handleOpenInGantt}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open in Gantt
                        </Button>
                        <Button
                          variant="outline"
                          className={theme === 'dark'
                            ? 'flex-1 border-[#111827] text-[#E5E7EB] hover:bg-[#050B17]'
                            : 'flex-1 border-gray-300 text-gray-700 hover:bg-gray-100'
                          }
                          onClick={handleGenerateRecovery}
                        >
                          Generate Recovery
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="impact" className="flex-1 overflow-y-auto p-6 space-y-6">
                    <ImpactVisualization insight={selectedInsightData} />
                  </TabsContent>

                  <TabsContent value="history" className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-3">
                      <div className={`text-sm ${
                        theme === 'dark' ? 'text-[#9CA3AF]' : 'text-gray-600'
                      }`}>
                        <div className={`font-medium mb-1  ${
                          theme === 'dark' ? 'text-[#E5E7EB]' : 'text-gray-700'
                        }`}>Created</div>
                        <div>{new Date(selectedInsightData.createdAt).toLocaleString()}</div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
